const rulesEngineContent = {
  title: "Rules Engine",
  description: "Create precise, deterministic rules for predictable file organization with exact conditions and actions.",
  sections: [
    {
      id: "overview",
      title: "Overview",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          The Rules Engine lets you create IF/THEN automation rules that execute the same way every time.
          Unlike AI sorting which interprets intent, deterministic rules follow exact conditions you specify—perfect
          for compliance workflows, automated pipelines, and situations where consistency is critical.
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">AI Sorting</h4>
            <p class="text-[#424245] text-sm">Interprets your intent, handles nuance, adapts to context. Great for flexible organization.</p>
          </div>
          <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Deterministic Rules</h4>
            <p class="text-[#424245] text-sm">Follows exact conditions, same inputs = same outputs. Great for repeatable workflows.</p>
          </div>
        </div>
      `
    },
    {
      id: "building-rules",
      title: "Building Rules",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">Rules have three components: conditions, logic operators, and actions.</p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="border-b border-[#e0e0e5] p-5">
            <h4 class="font-semibold text-[#1d1d1f]">Rule Builder Steps</h4>
          </div>

          <div class="p-6 space-y-5">
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3">
                  <span class="text-white font-medium text-sm">1</span>
                </div>
                Basic Info
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Set a name, description, and priority (lower numbers run first)</p>
            </div>

            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3">
                  <span class="text-white font-medium text-sm">2</span>
                </div>
                Conditions
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Define what files to match using field + comparator + value</p>
            </div>

            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3">
                  <span class="text-white font-medium text-sm">3</span>
                </div>
                Actions
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Specify what to do with matches: move, rename, tag, etc.</p>
            </div>

            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3">
                  <span class="text-white font-medium text-sm">4</span>
                </div>
                Test & Enable
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Use dry-run to preview results, then enable the rule</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: "fields",
      title: "Condition Fields",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Fields are properties of a file that you can test against. Select a field, then choose a comparator and value.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-5">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left border-b border-[#e0e0e5]">
                  <th class="pb-3 font-medium text-[#1d1d1f]">Field</th>
                  <th class="pb-3 font-medium text-[#1d1d1f]">Description</th>
                  <th class="pb-3 font-medium text-[#1d1d1f]">Example</th>
                </tr>
              </thead>
              <tbody class="text-[#424245]">
                <tr class="border-b border-[#e0e0e5]">
                  <td class="py-3 font-mono text-[#06c]">name</td>
                  <td class="py-3">Filename (with extension)</td>
                  <td class="py-3"><code class="bg-[#f5f5f7] px-1 rounded">invoice_2024.pdf</code></td>
                </tr>
                <tr class="border-b border-[#e0e0e5]">
                  <td class="py-3 font-mono text-[#06c]">extension</td>
                  <td class="py-3">File extension (without dot)</td>
                  <td class="py-3"><code class="bg-[#f5f5f7] px-1 rounded">pdf</code></td>
                </tr>
                <tr class="border-b border-[#e0e0e5]">
                  <td class="py-3 font-mono text-[#06c]">size</td>
                  <td class="py-3">File size in bytes</td>
                  <td class="py-3"><code class="bg-[#f5f5f7] px-1 rounded">104857600</code> (100MB)</td>
                </tr>
                <tr class="border-b border-[#e0e0e5]">
                  <td class="py-3 font-mono text-[#06c]">file_type</td>
                  <td class="py-3">General type category</td>
                  <td class="py-3"><code class="bg-[#f5f5f7] px-1 rounded">document</code>, <code class="bg-[#f5f5f7] px-1 rounded">image</code>, <code class="bg-[#f5f5f7] px-1 rounded">video</code></td>
                </tr>
                <tr class="border-b border-[#e0e0e5]">
                  <td class="py-3 font-mono text-[#06c]">mime_type</td>
                  <td class="py-3">MIME type</td>
                  <td class="py-3"><code class="bg-[#f5f5f7] px-1 rounded">application/pdf</code></td>
                </tr>
                <tr class="border-b border-[#e0e0e5]">
                  <td class="py-3 font-mono text-[#06c]">created_at</td>
                  <td class="py-3">File creation date</td>
                  <td class="py-3">Used with date comparators</td>
                </tr>
                <tr>
                  <td class="py-3 font-mono text-[#06c]">modified_at</td>
                  <td class="py-3">Last modified date</td>
                  <td class="py-3">Used with date comparators</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      `
    },
    {
      id: "comparators",
      title: "Comparators",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Comparators define how to test a field's value. There are 21 comparators organized by type.
        </p>

        <div class="space-y-4">
          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
              <h4 class="font-semibold text-[#1d1d1f]">String Comparators</h4>
            </div>
            <div class="p-4">
              <table class="w-full text-sm">
                <tbody class="text-[#424245]">
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c] w-40">EQUALS</td>
                    <td class="py-2">Exact match (case-sensitive option available)</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">NOT_EQUALS</td>
                    <td class="py-2">Does not match exactly</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">CONTAINS</td>
                    <td class="py-2">Value appears anywhere in field</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">NOT_CONTAINS</td>
                    <td class="py-2">Value does not appear in field</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">STARTS_WITH</td>
                    <td class="py-2">Field begins with value</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">ENDS_WITH</td>
                    <td class="py-2">Field ends with value</td>
                  </tr>
                  <tr>
                    <td class="py-2 font-mono text-[#06c]">MATCHES_REGEX</td>
                    <td class="py-2">Matches regular expression pattern</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
              <h4 class="font-semibold text-[#1d1d1f]">List Comparators</h4>
            </div>
            <div class="p-4">
              <table class="w-full text-sm">
                <tbody class="text-[#424245]">
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c] w-40">IN</td>
                    <td class="py-2">Value is in list (e.g., extension IN ["pdf", "docx"])</td>
                  </tr>
                  <tr>
                    <td class="py-2 font-mono text-[#06c]">NOT_IN</td>
                    <td class="py-2">Value is not in list</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
              <h4 class="font-semibold text-[#1d1d1f]">Numeric Comparators</h4>
            </div>
            <div class="p-4">
              <table class="w-full text-sm">
                <tbody class="text-[#424245]">
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c] w-40">GT</td>
                    <td class="py-2">Greater than (e.g., size GT 104857600 for >100MB)</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">GTE</td>
                    <td class="py-2">Greater than or equal</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">LT</td>
                    <td class="py-2">Less than</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">LTE</td>
                    <td class="py-2">Less than or equal</td>
                  </tr>
                  <tr>
                    <td class="py-2 font-mono text-[#06c]">BETWEEN</td>
                    <td class="py-2">Value is between two numbers (inclusive)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
              <h4 class="font-semibold text-[#1d1d1f]">Boolean Comparators</h4>
            </div>
            <div class="p-4">
              <table class="w-full text-sm">
                <tbody class="text-[#424245]">
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c] w-40">IS_TRUE</td>
                    <td class="py-2">Field value is true</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">IS_FALSE</td>
                    <td class="py-2">Field value is false</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">EXISTS</td>
                    <td class="py-2">Field has a value (not null/empty)</td>
                  </tr>
                  <tr>
                    <td class="py-2 font-mono text-[#06c]">NOT_EXISTS</td>
                    <td class="py-2">Field is null or empty</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
              <h4 class="font-semibold text-[#1d1d1f]">Special Comparators</h4>
            </div>
            <div class="p-4">
              <table class="w-full text-sm">
                <tbody class="text-[#424245]">
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c] w-40">HAS_TAG</td>
                    <td class="py-2">File has specific Finder tag (macOS)</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">MODIFIED_WITHIN</td>
                    <td class="py-2">Modified in last N days (e.g., "30d")</td>
                  </tr>
                  <tr>
                    <td class="py-2 font-mono text-[#06c]">CREATED_WITHIN</td>
                    <td class="py-2">Created in last N days</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      `
    },
    {
      id: "boolean-logic",
      title: "Boolean Logic",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Combine multiple conditions using boolean operators. You can nest condition groups for complex logic.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-5 space-y-4">
            <div class="flex items-start">
              <code class="bg-[#06c] text-white px-3 py-1 rounded font-mono text-sm mr-4 mt-0.5">ALL</code>
              <div>
                <p class="text-[#1d1d1f] font-medium">All conditions must match (AND)</p>
                <p class="text-[#424245] text-sm mt-1">Example: name CONTAINS "invoice" <strong>AND</strong> extension = "pdf"</p>
              </div>
            </div>
            <div class="flex items-start">
              <code class="bg-[#34c759] text-white px-3 py-1 rounded font-mono text-sm mr-4 mt-0.5">ANY</code>
              <div>
                <p class="text-[#1d1d1f] font-medium">At least one condition must match (OR)</p>
                <p class="text-[#424245] text-sm mt-1">Example: extension = "jpg" <strong>OR</strong> extension = "png"</p>
              </div>
            </div>
            <div class="flex items-start">
              <code class="bg-[#ff3b30] text-white px-3 py-1 rounded font-mono text-sm mr-3 mt-0.5">NONE</code>
              <div>
                <p class="text-[#1d1d1f] font-medium">No conditions can match (NOT)</p>
                <p class="text-[#424245] text-sm mt-1">Example: <strong>NOT</strong> (extension = "tmp" OR extension = "bak")</p>
              </div>
            </div>
          </div>
        </div>

        <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] mb-3">Nested Groups</h4>
          <p class="text-[#424245] text-sm mb-3">
            Create complex logic by nesting condition groups. For example:
          </p>
          <code class="block bg-[#1d1d1f] text-green-400 p-3 rounded-lg text-xs font-mono">
ALL OF:<br/>
&nbsp;&nbsp;- name CONTAINS "invoice"<br/>
&nbsp;&nbsp;- ANY OF:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;- extension = "pdf"<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;- extension = "docx"
          </code>
          <p class="text-[#424245] text-sm mt-3">
            This matches files with "invoice" in the name that are either PDF or DOCX.
          </p>
        </div>
      `
    },
    {
      id: "actions",
      title: "Actions",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          When a file matches your conditions, one or more actions execute in sequence.
        </p>

        <div class="space-y-4">
          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 flex items-center">
              <span class="bg-[#06c]/10 text-[#06c] px-2 py-1 rounded font-mono text-sm mr-3">MOVE</span>
              <h4 class="font-semibold text-[#1d1d1f]">Move File</h4>
            </div>
            <div class="p-4">
              <p class="text-[#424245] text-sm mb-2">Move file to a destination folder. Supports template variables for dynamic paths.</p>
              <code class="block bg-[#f5f5f7] p-2 rounded text-xs font-mono">Archive/{file.created}</code>
            </div>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 flex items-center">
              <span class="bg-[#06c]/10 text-[#06c] px-2 py-1 rounded font-mono text-sm mr-3">RENAME</span>
              <h4 class="font-semibold text-[#1d1d1f]">Rename File</h4>
            </div>
            <div class="p-4">
              <p class="text-[#424245] text-sm mb-2">Rename the file using a pattern with template variables.</p>
              <code class="block bg-[#f5f5f7] p-2 rounded text-xs font-mono">{file.name}_{file.created}{file.extension}</code>
            </div>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 flex items-center">
              <span class="bg-[#06c]/10 text-[#06c] px-2 py-1 rounded font-mono text-sm mr-3">TAG</span>
              <h4 class="font-semibold text-[#1d1d1f]">Add Tags</h4>
              <span class="ml-auto text-xs bg-[#f5f5f7] px-2 py-1 rounded text-[#86868b]">macOS only</span>
            </div>
            <div class="p-4">
              <p class="text-[#424245] text-sm">Add Finder tags to the file. Modes: add (append), set (replace), or remove.</p>
            </div>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl bg-[#f5f5f7]/60 backdrop-blur overflow-hidden">
            <div class="p-4">
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Sortio never deletes files</h4>
              <p class="text-[#424245] text-sm">There is no delete action. If you want files out of the way, move them to a folder such as <code>To Delete</code> and remove them yourself once you have checked them.</p>
            </div>
          </div>
        </div>

        <div class="border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur mt-6">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Sequential Actions
          </h4>
          <p class="text-[#424245] text-sm">Actions run in order. Enable "Continue on error" to proceed even if an action fails.</p>
        </div>
      `
    },
    {
      id: "variables",
      title: "Template Variables",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Use template variables in MOVE and RENAME actions to build dynamic paths and filenames.
        </p>

        <div class="space-y-4">
          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
              <h4 class="font-semibold text-[#1d1d1f]">File Variables</h4>
            </div>
            <div class="p-4">
              <table class="w-full text-sm">
                <tbody class="text-[#424245]">
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.name}</td>
                    <td class="py-2">Filename without the extension</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.extension}</td>
                    <td class="py-2">Extension, including the leading dot</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.path}</td>
                    <td class="py-2">Full path to the file</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.size}</td>
                    <td class="py-2">File size in bytes</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.type}</td>
                    <td class="py-2">MIME type</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.created}</td>
                    <td class="py-2">Creation timestamp</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.modified}</td>
                    <td class="py-2">Last modified timestamp</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.opened}</td>
                    <td class="py-2">Last opened timestamp</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.content}</td>
                    <td class="py-2">Extracted text preview, where available</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.macos.color}</td>
                    <td class="py-2">Finder color label (macOS)</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.image.width}</td>
                    <td class="py-2">Image width in pixels</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.image.height}</td>
                    <td class="py-2">Image height in pixels</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.exif.datetime}</td>
                    <td class="py-2">Date the photo was taken</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.exif.make}</td>
                    <td class="py-2">Camera make</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.exif.model}</td>
                    <td class="py-2">Camera model</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.exif.city}</td>
                    <td class="py-2">City from photo GPS data</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.exif.state}</td>
                    <td class="py-2">State or region from photo GPS data</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.exif.country}</td>
                    <td class="py-2">Country from photo GPS data</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.exif.latitude}</td>
                    <td class="py-2">GPS latitude</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{file.exif.longitude}</td>
                    <td class="py-2">GPS longitude</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
              <h4 class="font-semibold text-[#1d1d1f]">Knowledge Graph Variables</h4>
            </div>
            <div class="p-4">
              <p class="text-[#424245] text-sm mb-3">These read from the Knowledge Graph, so the folder must be indexed first. Always give them a fallback.</p>
              <table class="w-full text-sm">
                <tbody class="text-[#424245]">
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{entity.organization}</td>
                    <td class="py-2">Organization found in the file by the Knowledge Graph</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{entity.person}</td>
                    <td class="py-2">Person found in the file</td>
                  </tr>
                  <tr class="border-b border-[#e0e0e5]">
                    <td class="py-2 font-mono text-[#06c]">{entity.date}</td>
                    <td class="py-2">Date found in the file</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Give every variable a fallback</h4>
            <p class="text-[#424245] text-sm mb-3">Add <code>|</code> and a default so files missing that value still have somewhere to go. A file that cannot resolve a variable is skipped and reported; the rest of the run continues.</p>
            <code class="block bg-white p-2 rounded text-xs font-mono">Invoices/{entity.organization|Unfiled}</code>
          </div>
        </div>
      `
    },
    {
      id: "execution-modes",
      title: "Execution Modes",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Control when rules run relative to AI sorting in Smart Folders.
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">BeforeAi</h4>
            <p class="text-[#424245] text-sm">Rules run first. Matched files skip AI sorting. Use for files you always want handled a specific way.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">AfterAi</h4>
            <p class="text-[#424245] text-sm">AI sorts first, then rules post-process the results. Use for cleanup or additional organization.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">OnlyRules</h4>
            <p class="text-[#424245] text-sm">Skip AI entirely. Only deterministic rules run. Use when you want full manual control.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Disabled</h4>
            <p class="text-[#424245] text-sm">Rules don't run. AI handles everything. This is the default mode.</p>
          </div>
        </div>

        <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5 backdrop-blur mt-6">
          <h4 class="font-semibold text-[#1d1d1f] mb-2">Priority & Stop on Match</h4>
          <p class="text-[#424245] text-sm">
            Rules execute in priority order (lower numbers first). Enable "Stop on match" to prevent subsequent rules
            from processing a file after this rule matches.
          </p>
        </div>
      `
    },
    {
      id: "dry-run",
      title: "Dry-Run & Testing",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Always test rules before enabling them with dry-run mode.
        </p>

        <div class="border border-[#06c]/20 rounded-xl p-6 bg-[#06c]/5 backdrop-blur mb-6">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Dry-Run Mode
          </h4>
          <p class="text-[#424245]">
            Shows exactly which files match your conditions and what actions would be taken—without
            actually moving or modifying anything. Review the preview, adjust conditions if needed,
            then enable the rule when satisfied.
          </p>
        </div>

        <div class="border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Best Practices
          </h4>
          <ul class="text-[#424245] space-y-2 text-sm">
            <li>• Always dry-run new rules before enabling</li>
            <li>• Start with simple conditions, add complexity gradually</li>
            <li>• Set priorities carefully when rules might overlap</li>
            <li>• Use "Stop on match" to prevent rule conflicts</li>
            <li>• Check the activity log to verify rules work as expected</li>
          </ul>
        </div>
      `
    },
    {
      id: "examples",
      title: "Example Rules",
      content: `
        <div class="space-y-4">
          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
              <h4 class="font-semibold text-[#1d1d1f]">Invoice Organization</h4>
            </div>
            <div class="p-4">
              <p class="text-[#424245] text-sm mb-3">PDFs containing "invoice" → Finance/Invoices</p>
              <code class="block bg-[#1d1d1f] text-green-400 p-3 rounded-lg text-xs font-mono">
Conditions: ALL OF<br/>
&nbsp;&nbsp;- name CONTAINS "invoice" (case insensitive)<br/>
&nbsp;&nbsp;- extension IN ["pdf", "docx"]<br/><br/>
Actions:<br/>
&nbsp;&nbsp;1. MOVE → Finance/Invoices/{entity.organization|Unfiled}
              </code>
            </div>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
              <h4 class="font-semibold text-[#1d1d1f]">Screenshot Management</h4>
            </div>
            <div class="p-4">
              <p class="text-[#424245] text-sm mb-3">Screenshots → Screenshots, renamed with the date taken</p>
              <code class="block bg-[#1d1d1f] text-green-400 p-3 rounded-lg text-xs font-mono">
Conditions: ALL OF<br/>
&nbsp;&nbsp;- name STARTS_WITH "Screenshot"<br/>
&nbsp;&nbsp;- extension IN ["png", "jpg"]<br/><br/>
Actions:<br/>
&nbsp;&nbsp;1. MOVE → Screenshots<br/>
&nbsp;&nbsp;2. RENAME → {file.name}_{file.created}{file.extension}
              </code>
            </div>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
              <h4 class="font-semibold text-[#1d1d1f]">Large File Archival</h4>
            </div>
            <div class="p-4">
              <p class="text-[#424245] text-sm mb-3">Files over 100MB modified in last 30 days → Archive</p>
              <code class="block bg-[#1d1d1f] text-green-400 p-3 rounded-lg text-xs font-mono">
Conditions: ALL OF<br/>
&nbsp;&nbsp;- size GT 104857600 (100MB in bytes)<br/>
&nbsp;&nbsp;- modified_at MODIFIED_WITHIN "30d"<br/><br/>
Actions:<br/>
&nbsp;&nbsp;1. MOVE → Archive/{file.created}<br/>
&nbsp;&nbsp;2. TAG → ["archived", "large-file"]
              </code>
            </div>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
              <h4 class="font-semibold text-[#1d1d1f]">Photo Organization by EXIF Date</h4>
            </div>
            <div class="p-4">
              <p class="text-[#424245] text-sm mb-3">Organize photos by the date they were taken</p>
              <code class="block bg-[#1d1d1f] text-green-400 p-3 rounded-lg text-xs font-mono">
Conditions: ALL OF<br/>
&nbsp;&nbsp;- extension IN ["jpg", "jpeg", "png", "heic"]<br/><br/>
Actions:<br/>
&nbsp;&nbsp;1. MOVE → Photos/{file.exif.city|Unknown location}
              </code>
            </div>
          </div>
        </div>
      `
    }
  ]
};

export default rulesEngineContent;
